import { CrudFilters, parseTableParams } from "@pankod/refine-core";

import { IFeatureFlagFilters } from "interfaces/feature-flags";
import {
  buildFeatureFlagFilters,
  buildFeatureFlagSearchFormValues,
  FEATURE_FLAGS_PAGINATION_DEFAULTS,
} from "./feature-flags.filters";

const URL_PARAM_KEYS = {
  name: "ff_name",
  key: "ff_key",
  enabled: "ff_enabled",
  flagType: "ff_type",
  contexts: "ff_context",
  targetRepos: "ff_repo",
  tags: "ff_tags",
  expiresAt: "ff_exp",
  current: "ff_page",
} as const;

type FeatureFlagUrlPagination = {
  current?: number;
  pageSize?: number;
};

export type FeatureFlagListUrlState = {
  values: Partial<IFeatureFlagFilters>;
  filters: CrudFilters;
  hasSearchFilters: boolean;
  current: number;
  pageSize: number;
  searchKey: string;
};

const toPositiveNumber = (value: string | null | undefined): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) && parsedValue > 0
    ? parsedValue
    : undefined;
};

const toParsedPaginationNumber = (
  value: number | string | null | undefined,
): number | undefined => {
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }

  if (typeof value === "string") {
    return toPositiveNumber(value);
  }

  return undefined;
};

const buildSearchKey = (
  values: Partial<IFeatureFlagFilters>,
  pagination?: FeatureFlagUrlPagination,
) =>
  buildFeatureFlagListSearch(values, {
    current: pagination?.current ?? FEATURE_FLAGS_PAGINATION_DEFAULTS.initialCurrent,
    pageSize:
      pagination?.pageSize ?? FEATURE_FLAGS_PAGINATION_DEFAULTS.initialPageSize,
  });

const hasCompactUrlFilters = (params: URLSearchParams): boolean =>
  Object.values(URL_PARAM_KEYS).some((key) => params.has(key));

const getCompactUrlValues = (
  params: URLSearchParams,
): Partial<IFeatureFlagFilters> => {
  const enabledValue = params.get(URL_PARAM_KEYS.enabled);
  let enabled: boolean | undefined;

  if (enabledValue === "1") {
    enabled = true;
  } else if (enabledValue === "0") {
    enabled = false;
  }

  return {
    name: params.get(URL_PARAM_KEYS.name) ?? undefined,
    key: params.get(URL_PARAM_KEYS.key) ?? undefined,
    enabled,
    flag_type:
      (params.get(URL_PARAM_KEYS.flagType) as
        | IFeatureFlagFilters["flag_type"]
        | null) ?? undefined,
    contexts:
      (params.get(URL_PARAM_KEYS.contexts) as
        | IFeatureFlagFilters["contexts"]
        | null) ?? undefined,
    target_repos: params.get(URL_PARAM_KEYS.targetRepos) ?? undefined,
    tags: params.get(URL_PARAM_KEYS.tags) ?? undefined,
    expires_at: params.get(URL_PARAM_KEYS.expiresAt) ?? undefined,
  };
};

const getCompactPageSize = (params: URLSearchParams): number =>
  toPositiveNumber(params.get("ff_page_size")) ??
  FEATURE_FLAGS_PAGINATION_DEFAULTS.initialPageSize;

export const buildFeatureFlagListSearch = (
  values: Partial<IFeatureFlagFilters>,
  pagination?: FeatureFlagUrlPagination,
): string => {
  const filters = buildFeatureFlagFilters(values as IFeatureFlagFilters);

  if (!filters.length) {
    return "";
  }

  const params = new URLSearchParams();

  if (values.name?.trim()) {
    params.set(URL_PARAM_KEYS.name, values.name.trim());
  }

  if (values.key?.trim()) {
    params.set(URL_PARAM_KEYS.key, values.key.trim());
  }

  if (typeof values.enabled === "boolean") {
    params.set(URL_PARAM_KEYS.enabled, values.enabled ? "1" : "0");
  }

  if (values.flag_type) {
    params.set(URL_PARAM_KEYS.flagType, values.flag_type);
  }

  if (values.contexts) {
    params.set(URL_PARAM_KEYS.contexts, values.contexts);
  }

  if (typeof values.target_repos === "string" && values.target_repos.trim()) {
    params.set(URL_PARAM_KEYS.targetRepos, values.target_repos.trim());
  }

  if (typeof values.tags === "string" && values.tags.trim()) {
    params.set(URL_PARAM_KEYS.tags, values.tags.trim());
  }

  if (typeof values.expires_at === "string" && values.expires_at.trim()) {
    params.set(URL_PARAM_KEYS.expiresAt, values.expires_at.trim());
  }

  params.set(
    URL_PARAM_KEYS.current,
    String(
      pagination?.current ?? FEATURE_FLAGS_PAGINATION_DEFAULTS.initialCurrent,
    ),
  );

  return `?${params.toString()}`;
};

export const parseFeatureFlagListUrlState = (
  search: string,
): FeatureFlagListUrlState => {
  const params = new URLSearchParams(search);

  if (hasCompactUrlFilters(params)) {
    const values = getCompactUrlValues(params);
    const filters = buildFeatureFlagFilters(values as IFeatureFlagFilters);
    const current =
      toPositiveNumber(params.get(URL_PARAM_KEYS.current)) ??
      FEATURE_FLAGS_PAGINATION_DEFAULTS.initialCurrent;
    const pageSize = getCompactPageSize(params);

    return {
      values,
      filters,
      hasSearchFilters: filters.length > 0,
      current,
      pageSize,
      searchKey:
        filters.length > 0 ? buildSearchKey(values, { current, pageSize }) : "",
    };
  }

  const { parsedCurrent, parsedPageSize, parsedFilters } = parseTableParams(search);
  const values = buildFeatureFlagSearchFormValues(parsedFilters);
  const filters = buildFeatureFlagFilters(values as IFeatureFlagFilters);
  const current =
    toParsedPaginationNumber(parsedCurrent) ??
    FEATURE_FLAGS_PAGINATION_DEFAULTS.initialCurrent;
  const pageSize =
    toParsedPaginationNumber(parsedPageSize) ??
    FEATURE_FLAGS_PAGINATION_DEFAULTS.initialPageSize;
  const searchKey =
    filters.length > 0 ? buildSearchKey(values, { current, pageSize }) : "";

  return {
    values,
    filters,
    hasSearchFilters: filters.length > 0,
    current,
    pageSize,
    searchKey,
  };
};
